<template>
  <div class="w-full flex flex-column align-items-center mt-5" style="color: var(--theme-font-color);">
        <div style="height: 2.9rem;"></div>
        <span style="color: var(--theme-font-color); font-size: var(--login-maintitle-font-size); font-weight: var(--login-maintitle-font-weight);">Reset Password</span>
        
        <div :class="{ 'flex': !httpResetPasswordLoading, 'hidden': httpResetPasswordLoading}" style=" max-width: 100%;">
            <div class="mt-5 flex-column align-items-center" style="gap: 0.7rem; width: 32rem;" :class="{ 'flex': !resetPasswordErrorMessage, 'hidden': resetPasswordErrorMessage}">            
                <div class="registerlogin-section">
                    <div class="registerlogin-subsection relative">
                        <span class="subtitle">Password<span style="color: var(--mydanger-color);">*</span></span>
                        <PasswordInputText v-model:val="password" class="resetpassword-password" uid="password1Reset" @update-val="(val) => password=val" :isInvalid="errorPasswordMessage && isValidPasswordRegister(password)!=='success'"/>
                        <span v-if="errorPasswordMessage" class="error-message mb-2" style="color: var(--inputcomponent-invalid-color);">{{ errorPasswordMessage }}</span>
                    </div>
                </div>
                <div class="registerlogin-section gap-1">
                    <div class="registerlogin-subsection relative">
                        <span class="subtitle">Password Confirmation<span style="color: var(--mydanger-color);">*</span></span>
                        <PasswordInputText v-model:val="password2" class="resetpassword-password" uid="password2Reset" @update-val="(val) => password2=val" :isInvalid="password !== password2"/>
                        <span v-if="!errorPasswordMessage" class="error-message mb-2" style="color: var(--theme-font-color); opacity: 0.6;">{{ invalidPasswordMessage }}</span>
                    </div>
                </div>
                <TurnstileWidget widgetId="resetPasswordTurnstileWidget" @human-verified="(token) => {turnstileToken = token; isHumanVerified = true}"/>
                <div class="flex flex-column w-full gap-2 align-items-center">
                    <ButtonModule type="primary" severity="normal" label="Reset Password" :disabled="!enableResetPassword" @click="clickedResetPassword(password)"/>
                </div>
            </div>

            <div v-if="resetPasswordErrorMessage" class="w-full flex flex-column align-items-center justify-content-center mt-5" style="font-size: var(--text-font-size); font-weight: var(--text-font-weight);">
                <SvgError class="mb-4" className="resetpassword-svg" color="var(--mydanger-color)"/>
                <span class="text-center" style="font-size: var(--title-font-size); font-weight: var(--text-font-weight);">{{ resetPasswordErrorMessage }}</span>
                <span style="font-size: var(--title-font-size); font-weight: var(--text-font-weight); gap: 0.4rem;" class="text-center mt-2 align-items-center">
                    Please try to
                    <router-link unstyled :to="{ path: '/login'}" style="text-decoration: none;">
                        <span id="go-to-login-text" class="cursor-pointer">sign in</span>
                    </router-link>
                    again.
                </span>
            </div>
        </div>

        <div v-if="httpResetPasswordLoading" style="height: 65vh;" class="flex align-items-center justify-content-center">
            <LoadingSpinner/>
        </div>
  </div>
</template>

<script setup>
// --- IMPORTS ---
import { ref, toRefs, watchEffect, computed, onMounted, onBeforeUnmount } from 'vue';
// vue
import PasswordInputText from '@/components/Modules/PasswordInputText.vue'
import SvgError from '@/components/SVGs/SvgError.vue'
import LoadingSpinner from '@/components/Modules/LoadingSpinner.vue';
import TurnstileWidget from '@/components/Modules/TurnstileWidget.vue';
import ButtonModule from '@/components/Modules/ButtonModule.vue'
// Others
import InputsUtils from '@/composables/InputsUtils'
import toastService from '@/composables/toastService';
import { useRouter } from 'vue-router'
import { useStore } from 'vuex';

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchResetPassword } = useApiRequest()
const httpResetPasswordLoading = ref(false)
const httpResetPasswordData = ref(null)

const router = useRouter()
const { invalidPasswordMessage, isValidPasswordRegister } = InputsUtils()

// --- PROPS ---
const props = defineProps({
    token: String
})
const { token } = toRefs(props)

// --- REDIRECT IF NO ATTACHED TOKEN ---
watchEffect(() => {
    if (!token.value) { router.push({ path: '/login' }) }
})

// --- VARIABLES ---
const password = ref(null)
const password2 = ref(null)

// Errors
const errorPasswordMessage = ref(null)
const resetPasswordErrorMessage = ref(null)

// --- Click Reset Password ---
const enableResetPassword = computed(() => isHumanVerified.value && password.value && password2.value && (password.value === password2.value))
const clickedResetPassword = async (myPassword) => {
    errorPasswordMessage.value = null
    // Check if valid Password
    const isValidPasswordMessage = isValidPasswordRegister(myPassword)
    if (isValidPasswordMessage !== 'success') { errorPasswordMessage.value = isValidPasswordMessage; return; }

    // If the conditions are ok : go on
    errorPasswordMessage.value = null

    // Send HTTP request
    httpResetPasswordLoading.value = true
    try {
        httpResetPasswordData.value = await fetchResetPassword({ password_plain: myPassword, token: token.value, cf_turnstile_response : turnstileToken.value })
        // Reset of values
        password.value = null
        password2.value = null

        window.turnstile.reset("#resetPasswordTurnstileWidget")
        toastService.add({ severity: 'success', summary: "Reset Password: Success", detail: 'Your password has been successfully updated.', life: 5000 });
        // Go to /login
        router.push({ path: '/login' })
    } catch (err) {
        handleResetPasswordError(err)
    } finally {
        httpResetPasswordLoading.value = false
    }
}
// Handle an error from the server
const handleResetPasswordError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Reset Password: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (error.response.status) {
            case 400: // Missing info = MISSING_OBJ / MISSING_OBJ_ELEM (400) => toast
                if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') {
                    toastService.add({ severity: 'warn', summary: "Reset Password: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Reset Password: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 401: // The token attached to the request is not valid = TOKEN_INVALID (401) => view change
                if (errorCode == 'RESET_PASSWORD_TOKEN_INVALID') {
                    resetPasswordErrorMessage.value = "It seems that the password reset link is not valid anymore."
                }
                else {  toastService.add({ severity: 'error', summary: `Reset Password: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            case 404: // The token associated account doesn't exist. = ACCOUNT_NOT_FOUND (404) => view change
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    resetPasswordErrorMessage.value = "The account related to the password reset link seems to have been removed or does not exist."
                }
                else {  toastService.add({ severity: 'error', summary: `Reset Password: CAPTCHA Error.`, detail: "The CAPTCHA you entered is invalid or has expired. Please try again.", life: 5000 }); } // It only happens when the CAPTCHA is not correct
                break;
            case 500: // DATABASE_ERROR  (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Reset Password: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else {  toastService.add({ severity: 'error', summary: `Reset Password: Error ${errorStatus}.`, detail: errorMessage, life: 5000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Reset Password: Error ${errorStatus}.`, detail: errorMessage, life: 5000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Reset Password: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}

// --- ENTER key listener ---
onMounted(() => {
    window.addEventListener('keydown', handleKeyPressed)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyPressed)
}) 
const handleKeyPressed = (event) => {
    if (event.keyCode === 13) { // ENTER
        if (enableResetPassword.value) { clickedResetPassword(password.value) }
    }
}

// --- TURNSTILE ---
const turnstileToken = ref(null)
const isHumanVerified = ref(false)
</script>

<style scoped>
/* Label */
.subtitle {
    font-size: var(--login-subtitle-font-size);
    font-weight: var(--login-subtitle-font-weight);
    opacity: 0.7;
}
/* InputText */
:deep(.p-inputtext) {
    height: 2.5rem
}
/* Password input */
.resetpassword-password :deep(.p-inputtext), :deep(input::placeholder) {
    font-size: var(--login-inputtext-font-size);
    font-weight: var(--login-inputtext-font-weight);
}
/* Go to login */
#go-to-login-text {
    text-decoration: none;
    color: var(--button-normal-color);
}
#go-to-login-text:visited {
    color: var(--button-normal-color);
}
#go-to-login-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>